/**
 * Gestionnaire des avantages en nature conforme memo IUTS.md
 * Interface complète pour configuration et calculs automatiques
 */

import React, { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Tabs,
  Button,
  Table,
  Space,
  Tag,
  Statistic,
  Row,
  Col,
  Typography,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  message,
  Tooltip,
  Empty,
  Spin,
  Alert,
  Divider,
  Checkbox
} from 'antd';
import dayjs from 'dayjs';
import {
  PlusOutlined,
  HomeOutlined,
  CarOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  BookOutlined,
  SendOutlined,
  CalculatorOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { useHRAPI } from '../../hooks/useHRAPI';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

// Types TypeScript pour les avantages en nature
interface NaturaBenefit {
  id?: number;
  benefitType: 'housing' | 'vehicle' | 'domestic_help' | 'utilities' | 'education' | 'travel' | 'fuel' | 'other';
  description: string;
  calculationMethod: 'historical_240' | 'actual_cost' | 'fixed_amount' | 'percentage';
  
  // Valeurs pour calculs
  historicalCost?: number;
  monthlyActualCost?: number;
  fixedMonthlyAmount?: number;
  percentageRate?: number;
  calculatedMonthlyValue?: number;
  
  // Détails spécifiques logement
  housingSurfaceM2?: number;
  housingRoomsCount?: number;
  housingAddress?: string;
  
  // Détails spécifiques véhicule
  vehicleBrand?: string;
  vehicleModel?: string;
  vehicleYear?: number;
  vehicleRegistration?: string;
  
  // Détails spécifiques domesticité
  domesticEmployeeCount?: number;
  domesticTotalSalary?: number;
  domesticCnssEmployer?: number;
  domesticTpa?: number;
  domesticOtherCosts?: number;
  
  // Détails spécifiques utilities
  utilityType?: 'water' | 'electricity' | 'phone' | 'internet' | 'cable_tv' | 'mixed';
  
  // Détails spécifiques éducation
  educationChildCount?: number;
  educationAnnualCost?: number;
  educationInstitution?: string;
  
  // Périodes
  startDate: string;
  endDate?: string;
  isActive: boolean;
  
  // Contrôle
  requiresApproval: boolean;
  approvedBy?: number;
  approvedAt?: string;
}

interface FiscalImpactPreview {
  currentMonthlyValue: number;
  cnssImpact: number;
  iutsImpact: number;
  totalFiscalImpact: number;
  employerCostImpact: number;
}

interface NaturaBenefitsManagerProps {
  employeeId: number;
  companyId: number;
  employeeData?: {
    id: number;
    firstName: string;
    lastName: string;
    basicSalary: number;
    sector: 'public' | 'private';
  };
}

const NaturaBenefitsManager: React.FC<NaturaBenefitsManagerProps> = ({
  employeeId,
  companyId,
  employeeData
}) => {
  // Hook API pour les requêtes authentifiées
  const {
    getNaturaBenefits,
    calculateFiscalPreview: calculateFiscalPreviewAPI,
    createNaturaBenefit,
    updateNaturaBenefit,
    deleteNaturaBenefit,
    getEmployee,
    getContracts,
    getEmployeePayrollItems,
    loading: apiLoading,
    error: apiError
  } = useHRAPI();

  // États du composant
  const [benefits, setBenefits] = useState<NaturaBenefit[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedBenefit, setSelectedBenefit] = useState<NaturaBenefit | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [fiscalPreview, setFiscalPreview] = useState<FiscalImpactPreview | null>(null);
  const [activeTab, setActiveTab] = useState('list');
  const [currentEmployeeData, setCurrentEmployeeData] = useState(employeeData);
  
  // Form instance pour Ant Design
  const [form] = Form.useForm();

  // Configuration des types d'avantages
  const benefitTypes = [
    { 
      value: 'housing', 
      label: 'Logement de fonction', 
      icon: HomeOutlined,
      color: 'blue',
      description: 'Villa, appartement, logement avec accessoires'
    },
    { 
      value: 'vehicle', 
      label: 'Véhicule de fonction', 
      icon: CarOutlined,
      color: 'green',
      description: 'Véhicule mis à disposition'
    },
    { 
      value: 'domestic_help', 
      label: 'Domesticité', 
      icon: TeamOutlined,
      color: 'purple',
      description: 'Gardien, jardinier, employés de maison'
    },
    { 
      value: 'utilities', 
      label: 'Utilities', 
      icon: ThunderboltOutlined,
      color: 'orange',
      description: 'Eau, électricité, téléphone, internet'
    },
    { 
      value: 'education', 
      label: 'Scolarité', 
      icon: BookOutlined,
      color: 'cyan',
      description: 'Frais de scolarité des enfants'
    },
    { 
      value: 'travel', 
      label: 'Frais de voyage', 
      icon: SendOutlined,
      color: 'magenta',
      description: 'Voyage pour congés personnel local'
    }
  ];

  // Méthodes de calcul
  const calculationMethods = [
    {
      value: 'historical_240',
      label: '1/240e du coût historique',
      description: 'Méthode réglementaire standard',
      isDefault: true
    },
    {
      value: 'actual_cost',
      label: 'Coût réel mensuel',
      description: 'Pour utilities et frais variables'
    },
    {
      value: 'fixed_amount',
      label: 'Montant forfaitaire',
      description: 'Montant fixe mensuel'
    },
    {
      value: 'percentage',
      label: 'Pourcentage du salaire',
      description: 'Calculé sur le salaire de base'
    }
  ];

  // Fetch employee data if not provided
  const fetchEmployeeData = async () => {
    if (!currentEmployeeData || !currentEmployeeData.basicSalary) {
      try {
        const employee = await getEmployee(employeeId);
        let basicSalary = 0;
        
        if (employee) {
          console.log(`[NaturaBenefitsManager] Fetching salary data for employee ${employeeId}`);
          
          // Method 1: Try to get basic salary from active contract
          try {
            const contracts = await getContracts({ employee_id: employeeId, status: 'active' });
            console.log(`[NaturaBenefitsManager] Found ${contracts?.length || 0} active contracts`);
            if (contracts && contracts.length > 0) {
              const activeContract = contracts[0];
              basicSalary = activeContract.base_salary || activeContract.gross_salary || 0;
              console.log(`[NaturaBenefitsManager] Contract salary: ${basicSalary} FCFA`);
            }
          } catch (contractError) {
            console.warn('Could not fetch employee contract:', contractError);
          }

          // Method 2: If no salary from contract, try to calculate from payroll items
          if (basicSalary === 0) {
            console.log(`[NaturaBenefitsManager] Contract salary is 0, checking payroll items...`);
            try {
              const payrollItems = await getEmployeePayrollItems(employeeId);
              console.log(`[NaturaBenefitsManager] Found ${payrollItems?.length || 0} payroll items`);
              
              if (payrollItems && payrollItems.length > 0) {
                console.log('[NaturaBenefitsManager] Payroll items:', payrollItems.map(item => ({
                  name: item.name,
                  category: item.category,
                  type: item.type,
                  custom_amount: item.custom_amount,
                  default_amount: item.default_amount,
                  is_active: item.is_active
                })));
                
                // Calculate basic salary from salary-category payroll items
                const salaryItems = payrollItems.filter(item => 
                  item.category === 'salary' && 
                  item.type === 'gain' && 
                  item.is_active
                );
                
                console.log(`[NaturaBenefitsManager] Found ${salaryItems.length} salary items`);
                
                basicSalary = salaryItems.reduce((total, item) => {
                  // Use custom amount if available, otherwise use default amount
                  const amount = item.custom_amount || item.default_amount || 0;
                  console.log(`[NaturaBenefitsManager] Adding ${amount} FCFA from ${item.name || item.code}`);
                  return total + amount;
                }, 0);
                
                console.log(`[NaturaBenefitsManager] Total calculated salary: ${basicSalary} FCFA`);
              }
            } catch (payrollError) {
              console.warn('Could not fetch employee payroll items:', payrollError);
            }
          }

          setCurrentEmployeeData({
            id: employee.id,
            firstName: employee.first_name,
            lastName: employee.last_name,
            basicSalary: basicSalary,
            sector: 'private' // Default fallback - this could be enhanced later
          });
        }
      } catch (error) {
        console.warn('Could not fetch employee data:', error);
      }
    }
  };

  // Chargement des avantages existants
  useEffect(() => {
    const initializeData = async () => {
      await fetchEmployeeData();
      await loadBenefits();
    };
    initializeData();
  }, [employeeId, companyId]);

  const loadBenefits = async () => {
    setLoading(true);
    try {
      // Utilisation de l'API authentifiée
      const data = await getNaturaBenefits(employeeId, companyId);
      
      if (data?.success) {
        // Mapper les propriétés de la BDD vers l'interface TypeScript
        const mappedBenefits = (data.data || []).map((benefit: any) => ({
          ...benefit,
          // Mapping des propriétés camelCase pour cohérence avec l'interface
          calculatedMonthlyValue: benefit.calculated_monthly_value,
          benefitType: benefit.benefit_type,
          calculationMethod: benefit.calculation_method,
          historicalCost: benefit.historical_cost,
          monthlyActualCost: benefit.monthly_actual_cost,
          fixedMonthlyAmount: benefit.fixed_monthly_amount,
          percentageRate: benefit.percentage_rate,
          housingSurfaceM2: benefit.housing_surface_m2,
          housingRoomsCount: benefit.housing_rooms_count,
          housingAddress: benefit.housing_address,
          vehicleBrand: benefit.vehicle_brand,
          vehicleModel: benefit.vehicle_model,
          vehicleYear: benefit.vehicle_year,
          vehicleRegistration: benefit.vehicle_registration,
          domesticEmployeeCount: benefit.domestic_employee_count,
          domesticTotalSalary: benefit.domestic_total_salary,
          domesticCnssEmployer: benefit.domestic_cnss_employer,
          domesticTpa: benefit.domestic_tpa,
          domesticOtherCosts: benefit.domestic_other_costs,
          utilityType: benefit.utility_type,
          educationChildCount: benefit.education_child_count,
          educationAnnualCost: benefit.education_annual_cost,
          educationInstitution: benefit.education_institution,
          startDate: benefit.start_date,
          endDate: benefit.end_date,
          isActive: Boolean(benefit.is_active),
          requiresApproval: Boolean(benefit.requires_approval),
          approvedBy: benefit.approved_by,
          approvedAt: benefit.approved_at
        }));
        
        setBenefits(mappedBenefits);
        
        // Calculer l'aperçu fiscal global
        if (mappedBenefits.length > 0) {
          await calculateFiscalPreview();
        }
      }
    } catch (error) {
      console.error('Erreur chargement avantages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calcul de l'aperçu d'impact fiscal
  const calculateFiscalPreview = async () => {
    try {
      // Vérifier si on a les données employé nécessaires
      const basicSalary = currentEmployeeData?.basicSalary || 0;
      
      // Skip fiscal preview if basic salary is not available
      if (!basicSalary || basicSalary <= 0) {
        console.warn('Cannot calculate fiscal preview: basic salary not available or is 0');
        setFiscalPreview(null);
        return;
      }
      
      // Utilisation de l'API authentifiée
      const data = await calculateFiscalPreviewAPI(
        employeeId,
        companyId,
        basicSalary,
        currentEmployeeData?.sector || 'private'
      );
      if (data?.success) {
        // Adapter les données de la réponse à notre interface
        const adaptedData: FiscalImpactPreview = {
          currentMonthlyValue: data.data.withNaturaBenefits?.naturaBenefitsValue || 0,
          cnssImpact: data.data.impacts?.cnssEmployeeIncrease || 0,
          iutsImpact: data.data.impacts?.iutsIncrease || 0,
          totalFiscalImpact: data.data.impacts?.totalFiscalImpact || 0,
          employerCostImpact: data.data.impacts?.employerCostIncrease || 0
        };
        setFiscalPreview(adaptedData);
      }
    } catch (error) {
      console.error('Erreur calcul aperçu fiscal:', error);
    }
  };

  // Calcul de valeur mensuelle selon méthode
  const calculateMonthlyValue = (benefit: Partial<NaturaBenefit>): number => {
    let value = 0;
    
    switch (benefit.calculationMethod) {
      case 'historical_240':
        value = (benefit.historicalCost || 0) / 240;
        break;
      case 'actual_cost':
        // Calcul spécial pour domesticité
        if (benefit.benefitType === 'domestic_help') {
          value = (benefit.domesticTotalSalary || 0) +
                 (benefit.domesticCnssEmployer || 0) +
                 (benefit.domesticTpa || 0) +
                 (benefit.domesticOtherCosts || 0);
        } else {
          value = benefit.monthlyActualCost || 0;
        }
        break;
      case 'fixed_amount':
        value = benefit.fixedMonthlyAmount || 0;
        break;
      case 'percentage':
        value = ((currentEmployeeData?.basicSalary || 0) * (benefit.percentageRate || 0)) / 100;
        break;
      default:
        value = 0;
    }
    
    // Calcul spécial pour éducation : conversion annuel -> mensuel
    if (benefit.benefitType === 'education' && benefit.educationAnnualCost) {
      value = benefit.educationAnnualCost / 12;
    }
    
    return Math.round(value);
  };

  // Sauvegarde d'un avantage
  const saveBenefit = async (benefit: NaturaBenefit) => {
    try {
      const benefitData = {
        ...benefit,
        companyId,
        // Recalculer automatiquement la valeur mensuelle
        calculated_monthly_value: calculateMonthlyValue(benefit),
        // Convertir les propriétés camelCase vers snake_case pour la BDD
        benefit_type: benefit.benefitType,
        calculation_method: benefit.calculationMethod,
        historical_cost: benefit.historicalCost,
        monthly_actual_cost: benefit.monthlyActualCost,
        fixed_monthly_amount: benefit.fixedMonthlyAmount,
        percentage_rate: benefit.percentageRate,
        housing_surface_m2: benefit.housingSurfaceM2,
        housing_rooms_count: benefit.housingRoomsCount,
        housing_address: benefit.housingAddress,
        vehicle_brand: benefit.vehicleBrand,
        vehicle_model: benefit.vehicleModel,
        vehicle_year: benefit.vehicleYear,
        vehicle_registration: benefit.vehicleRegistration,
        domestic_employee_count: benefit.domesticEmployeeCount,
        domestic_total_salary: benefit.domesticTotalSalary,
        domestic_cnss_employer: benefit.domesticCnssEmployer,
        domestic_tpa: benefit.domesticTpa,
        domestic_other_costs: benefit.domesticOtherCosts,
        utility_type: benefit.utilityType,
        education_child_count: benefit.educationChildCount,
        education_annual_cost: benefit.educationAnnualCost,
        education_institution: benefit.educationInstitution,
        start_date: benefit.startDate,
        end_date: benefit.endDate,
        is_active: benefit.isActive ? 1 : 0,
        requires_approval: benefit.requiresApproval ? 1 : 0
      };

      let data;
      if (benefit.id) {
        // Modification d'un avantage existant
        data = await updateNaturaBenefit(benefit.id, benefitData);
      } else {
        // Création d'un nouvel avantage
        data = await createNaturaBenefit(employeeId, benefitData);
      }
      if (data?.success) {
        message.success(benefit.id 
          ? 'Avantage modifié avec succès' 
          : 'Avantage créé avec succès'
        );
        await loadBenefits(); // Recharger la liste
        setShowAddForm(false);
        setSelectedBenefit(null);
      } else {
        message.error(data.message || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur sauvegarde avantage:', error);
      message.error('Erreur lors de la sauvegarde de l\'avantage');
    }
  };

  // Suppression d'un avantage
  const deleteBenefit = async (benefitId: number) => {
    Modal.confirm({
      title: 'Supprimer cet avantage',
      content: 'Êtes-vous sûr de vouloir supprimer cet avantage en nature ? Cette action est irréversible.',
      icon: <ExclamationCircleOutlined />,
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          // Utilisation de l'API authentifiée
          const data = await deleteNaturaBenefit(benefitId, companyId);
          
          if (data?.success) {
            message.success('Avantage supprimé avec succès');
            await loadBenefits();
          } else {
            message.error(data?.message || 'Erreur lors de la suppression');
          }
        } catch (error) {
          console.error('Erreur suppression avantage:', error);
          message.error('Erreur lors de la suppression de l\'avantage');
        }
      }
    });
  };

  // Rendu des cartes d'avantages
  const renderBenefitCard = (benefit: NaturaBenefit) => {
    const benefitTypeConfig = benefitTypes.find(bt => bt.value === benefit.benefitType);
    const Icon = benefitTypeConfig?.icon || HomeOutlined;
    
    return (
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon style={{ fontSize: 18, color: '#666' }} />
            <span style={{ fontSize: 16 }}>{benefit.description}</span>
          </div>
        }
        extra={
          <Space>
            <Tag color={benefitTypeConfig?.color}>
              {benefitTypeConfig?.label}
            </Tag>
            {benefit.requiresApproval && !benefit.approvedAt && (
              <Tag color="orange">En attente</Tag>
            )}
            {benefit.approvedAt && (
              <Tag color="green" icon={<CheckCircleOutlined />}>
                Approuvé
              </Tag>
            )}
          </Space>
        }
        actions={[
          <Tooltip title="Voir l'aperçu">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedBenefit(benefit);
                setActiveTab('preview');
              }}
            />
          </Tooltip>,
          <Tooltip title="Modifier">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedBenefit(benefit);
                setShowAddForm(true);
              }}
            />
          </Tooltip>,
          <Tooltip title="Supprimer">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => benefit.id && deleteBenefit(benefit.id)}
            />
          </Tooltip>
        ]}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/* Valeur mensuelle */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center' 
          }}>
            <Text type="secondary">Valeur mensuelle imposable:</Text>
            <Text strong style={{ fontSize: 16, color: '#1890ff' }}>
              {(benefit.calculatedMonthlyValue || 0).toLocaleString()} FCFA
            </Text>
          </div>
          
          {/* Méthode de calcul */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center' 
          }}>
            <Text type="secondary">Méthode:</Text>
            <Text>
              {calculationMethods.find(cm => cm.value === benefit.calculationMethod)?.label}
            </Text>
          </div>
          
          {/* Détails spécifiques selon le type */}
          {benefit.benefitType === 'housing' && benefit.housingSurfaceM2 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {benefit.housingSurfaceM2} m² • {benefit.housingRoomsCount} pièces
            </Text>
          )}
          
          {benefit.benefitType === 'vehicle' && benefit.vehicleBrand && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {benefit.vehicleBrand} {benefit.vehicleModel} ({benefit.vehicleYear})
            </Text>
          )}
          
          {benefit.benefitType === 'domestic_help' && benefit.domesticEmployeeCount && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {benefit.domesticEmployeeCount} employé(s) • {(benefit.domesticTotalSalary || 0).toLocaleString()} FCFA
            </Text>
          )}
          
          {/* Période */}
          <Text type="secondary" style={{ fontSize: 12 }}>
            Du {new Date(benefit.startDate).toLocaleDateString()} 
            {benefit.endDate && ` au ${new Date(benefit.endDate).toLocaleDateString()}`}
          </Text>
        </Space>
      </Card>
    );
  };

  // Gestion des onglets
  const handleTabChange = (tabValue: string) => {
    setActiveTab(tabValue);
  };

  // Composant principal
  return (
    <Layout>
      <Content style={{ padding: "24px" }}>
        {/* En-tête */}
        <Card className="mb-6" bordered={false}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Title level={2} style={{ margin: 0 }}>Avantages en Nature</Title>
              <Text type="secondary">
                Gestion conforme arrêté 2023-0017/MFPTPS/SG/DGPS • Méthode 1/240e
              </Text>
              {currentEmployeeData && (
                <div style={{ marginTop: 8 }}>
                  <Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Employé: {currentEmployeeData.firstName} {currentEmployeeData.lastName}
                    </Text>
                    <Text type="secondary">•</Text>
                    <Tag color={currentEmployeeData.sector === 'public' ? 'blue' : 'green'}>
                      Secteur {currentEmployeeData.sector === 'public' ? 'Public' : 'Privé'}
                    </Tag>
                  </Space>
                </div>
              )}
            </div>
            
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={() => setShowAddForm(true)}
              disabled={loading || apiLoading}
            >
              Nouvel Avantage
            </Button>
          </div>
        </Card>

        {/* Affichage des erreurs API */}
        {apiError && (
          <Alert
            message="Erreur API"
            description={apiError}
            type="error"
            showIcon
            closable
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Warning for missing basic salary */}
        {(!currentEmployeeData?.basicSalary || currentEmployeeData?.basicSalary <= 0) && (
          <Alert
            message="Informations employé incomplètes"
            description="Le salaire de base de l'employé n'est pas disponible. L'aperçu fiscal ne peut pas être calculé."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Aperçu fiscal global */}
        {fiscalPreview && (
          <Alert
            message="Résumé de l'impact fiscal"
            description={
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={24} sm={12} lg={6}>
                  <Statistic
                    title="Valeur mensuelle totale"
                    value={fiscalPreview.currentMonthlyValue}
                    suffix="FCFA"
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Statistic
                    title="Impact CNSS"
                    value={fiscalPreview.cnssImpact}
                    prefix="+"
                    suffix="FCFA"
                    valueStyle={{ color: '#1890ff' }}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Statistic
                    title="Impact IUTS"
                    value={fiscalPreview.iutsImpact}
                    prefix="+"
                    suffix="FCFA"
                    valueStyle={{ color: '#722ed1' }}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Statistic
                    title="Coût employeur"
                    value={fiscalPreview.employerCostImpact}
                    prefix="+"
                    suffix="FCFA"
                    valueStyle={{ color: '#f5222d' }}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  />
                </Col>
              </Row>
            }
            type="info"
            showIcon
            icon={<CalculatorOutlined />}
            className="mb-6"
          />
        )}

        {/* Onglets principaux */}
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={[
            {
              key: 'list',
              label: 'Liste des Avantages',
              children: (
                <div style={{ padding: '16px 0' }}>
                  {(loading || apiLoading) ? (
                    <div style={{ textAlign: 'center', padding: '64px 0' }}>
                      <Spin size="large" />
                      <div style={{ marginTop: 16 }}>Chargement...</div>
                    </div>
                  ) : benefits.length === 0 ? (
                    <Empty
                      image={<ExclamationCircleOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
                      description={
                        <div>
                          <Title level={4} style={{ color: '#666' }}>Aucun avantage configuré</Title>
                          <Text type="secondary">
                            Commencez par ajouter un avantage en nature pour cet employé
                          </Text>
                        </div>
                      }
                    >
                      <Button 
                        type="primary" 
                        icon={<PlusOutlined />} 
                        onClick={() => setShowAddForm(true)}
                      >
                        Ajouter le premier avantage
                      </Button>
                    </Empty>
                  ) : (
                    <Row gutter={[16, 16]}>
                      {benefits.map((benefit) => (
                        <Col xs={24} sm={12} lg={8} key={benefit.id}>
                          {renderBenefitCard(benefit)}
                        </Col>
                      ))}
                    </Row>
                  )}
                </div>
              )
            },
            {
              key: 'summary',
              label: 'Résumé Fiscal',
              children: (
                <Card title="Résumé Fiscal Mensuel">
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    {benefitTypes.map(type => {
                      const typeBenefits = benefits.filter(b => b.benefitType === type.value && b.isActive);
                      const totalValue = typeBenefits.reduce((sum, b) => sum + (b.calculatedMonthlyValue || 0), 0);
                      
                      if (totalValue === 0) return null;
                      
                      const Icon = type.icon;
                      
                      return (
                        <Card 
                          key={type.value} 
                          size="small"
                          style={{ backgroundColor: '#fafafa' }}
                        >
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center' 
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <Icon style={{ fontSize: 20, color: '#666' }} />
                              <div>
                                <div style={{ fontWeight: 500 }}>{type.label}</div>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  {typeBenefits.length} avantage(s)
                                </Text>
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <Text strong style={{ fontSize: 16 }}>
                                {totalValue.toLocaleString()} FCFA
                              </Text>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </Space>
                </Card>
              )
            },
            {
              key: 'preview',
              label: 'Aperçu Impact',
              children: (
                <div style={{ padding: '16px 0' }}>
                  {fiscalPreview ? (
                    <Row gutter={[24, 24]}>
                      {/* Résumé général */}
                      <Col xs={24}>
                        <Card 
                          title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <CalculatorOutlined style={{ color: '#1890ff' }} />
                              <span>Impact Fiscal Détaillé</span>
                            </div>
                          }
                          style={{ marginBottom: 16 }}
                        >
                          <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} lg={6}>
                              <div style={{ textAlign: 'center', padding: '16px' }}>
                                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>
                                  {fiscalPreview.currentMonthlyValue.toLocaleString()} FCFA
                                </div>
                                <div style={{ color: '#666', marginTop: 4 }}>
                                  Valeur mensuelle imposable
                                </div>
                              </div>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                              <div style={{ textAlign: 'center', padding: '16px' }}>
                                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#52c41a' }}>
                                  +{fiscalPreview.cnssImpact.toLocaleString()} FCFA
                                </div>
                                <div style={{ color: '#666', marginTop: 4 }}>
                                  Augmentation CNSS employé
                                </div>
                              </div>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                              <div style={{ textAlign: 'center', padding: '16px' }}>
                                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#722ed1' }}>
                                  +{fiscalPreview.iutsImpact.toLocaleString()} FCFA
                                </div>
                                <div style={{ color: '#666', marginTop: 4 }}>
                                  Augmentation IUTS
                                </div>
                              </div>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                              <div style={{ textAlign: 'center', padding: '16px' }}>
                                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#f5222d' }}>
                                  +{fiscalPreview.employerCostImpact.toLocaleString()} FCFA
                                </div>
                                <div style={{ color: '#666', marginTop: 4 }}>
                                  Coût supplémentaire employeur
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </Card>
                      </Col>

                      {/* Détail par avantage */}
                      <Col xs={24} lg={16}>
                        <Card title="Détail par avantage en nature">
                          {benefits.filter(b => b.isActive).length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                              {benefits.filter(b => b.isActive).map((benefit) => {
                                const benefitTypeConfig = benefitTypes.find(bt => bt.value === benefit.benefitType);
                                const Icon = benefitTypeConfig?.icon || HomeOutlined;
                                const monthlyValue = benefit.calculatedMonthlyValue || 0;
                                const cnssImpact = monthlyValue * 0.055; // 5.5% CNSS employé
                                const iutsImpact = monthlyValue * 0.10; // 10% IUTS approximatif
                                
                                return (
                                  <Card 
                                    key={benefit.id} 
                                    size="small" 
                                    style={{ backgroundColor: '#fafafa' }}
                                  >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <Icon style={{ fontSize: 20, color: '#666' }} />
                                        <div>
                                          <div style={{ fontWeight: 500 }}>{benefit.description}</div>
                                          <Text type="secondary" style={{ fontSize: 12 }}>
                                            {benefitTypeConfig?.label} • 
                                            {calculationMethods.find(cm => cm.value === benefit.calculationMethod)?.label}
                                          </Text>
                                        </div>
                                      </div>
                                      <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontWeight: 'bold', fontSize: 16 }}>
                                          {monthlyValue.toLocaleString()} FCFA
                                        </div>
                                        <div style={{ fontSize: 12, color: '#666' }}>
                                          CNSS: +{Math.round(cnssImpact).toLocaleString()} • 
                                          IUTS: +{Math.round(iutsImpact).toLocaleString()}
                                        </div>
                                      </div>
                                    </div>
                                  </Card>
                                );
                              })}
                            </div>
                          ) : (
                            <Empty 
                              description="Aucun avantage actif pour calculer l'impact"
                              image={<ExclamationCircleOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />}
                            />
                          )}
                        </Card>
                      </Col>

                      {/* Explications réglementaires */}
                      <Col xs={24} lg={8}>
                        <Card title="Cadre réglementaire">
                          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <Alert
                              message="Méthode 1/240e"
                              description="La valeur mensuelle imposable est calculée selon l'arrêté 2023-0017/MFPTPS/SG/DGPS du Burkina Faso"
                              type="info"
                              showIcon
                              style={{ fontSize: 12 }}
                            />
                            
                            <div style={{ padding: '12px', backgroundColor: '#f9f9f9', borderRadius: 6 }}>
                              <Text strong style={{ fontSize: 14 }}>Taux appliqués :</Text>
                              <div style={{ marginTop: 8 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                  <Text style={{ fontSize: 12 }}>CNSS employé :</Text>
                                  <Text style={{ fontSize: 12 }}>5,5%</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                  <Text style={{ fontSize: 12 }}>CNSS employeur :</Text>
                                  <Text style={{ fontSize: 12 }}>16%</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                  <Text style={{ fontSize: 12 }}>IUTS :</Text>
                                  <Text style={{ fontSize: 12 }}>Variable selon tranches</Text>
                                </div>
                              </div>
                            </div>

                            <div style={{ padding: '12px', backgroundColor: '#fff7e6', borderRadius: 6, border: '1px solid #ffd591' }}>
                              <Text strong style={{ fontSize: 14, color: '#d46b08' }}>
                                Note importante :
                              </Text>
                              <div style={{ marginTop: 8, fontSize: 12, color: '#d46b08' }}>
                                Les calculs sont estimatifs. Consultez un expert-comptable pour validation finale.
                              </div>
                            </div>

                            {currentEmployeeData && (
                              <div style={{ padding: '12px', backgroundColor: '#f6ffed', borderRadius: 6, border: '1px solid #b7eb8f' }}>
                                <Text strong style={{ fontSize: 14 }}>Employé :</Text>
                                <div style={{ marginTop: 8, fontSize: 12 }}>
                                  <div>Salaire de base: {(currentEmployeeData.basicSalary || 0).toLocaleString()} FCFA</div>
                                  <div>Secteur: {currentEmployeeData.sector === 'public' ? 'Public' : 'Privé'}</div>
                                </div>
                              </div>
                            )}
                          </Space>
                        </Card>
                      </Col>

                      {/* Simulation annuelle */}
                      <Col xs={24}>
                        <Card title="Projection annuelle">
                          <Row gutter={[16, 16]}>
                            <Col xs={24} sm={6}>
                              <Statistic
                                title="Avantages annuels"
                                value={fiscalPreview.currentMonthlyValue * 12}
                                suffix="FCFA"
                                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                valueStyle={{ color: '#1890ff' }}
                              />
                            </Col>
                            <Col xs={24} sm={6}>
                              <Statistic
                                title="CNSS supplémentaire/an"
                                value={fiscalPreview.cnssImpact * 12}
                                suffix="FCFA"
                                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                valueStyle={{ color: '#52c41a' }}
                              />
                            </Col>
                            <Col xs={24} sm={6}>
                              <Statistic
                                title="IUTS supplémentaire/an"
                                value={fiscalPreview.iutsImpact * 12}
                                suffix="FCFA"
                                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                valueStyle={{ color: '#722ed1' }}
                              />
                            </Col>
                            <Col xs={24} sm={6}>
                              <Statistic
                                title="Coût employeur/an"
                                value={fiscalPreview.employerCostImpact * 12}
                                suffix="FCFA"
                                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                valueStyle={{ color: '#f5222d' }}
                              />
                            </Col>
                          </Row>
                        </Card>
                      </Col>
                    </Row>
                  ) : (
                    <Empty
                      description="Aucune donnée d'impact fiscal disponible"
                      image={<CalculatorOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
                    >
                      <Text type="secondary">
                        Ajoutez des avantages en nature pour voir l'aperçu de l'impact fiscal
                      </Text>
                    </Empty>
                  )}
                </div>
              )
            }
          ]}
        />

        {/* Modal pour ajout/modification */}
        <Modal 
          title={`${selectedBenefit ? 'Modifier' : 'Nouvel'} Avantage en Nature`}
          open={showAddForm}
          onCancel={() => {
            setShowAddForm(false);
            setSelectedBenefit(null);
            form.resetFields();
          }}
          width={800}
          footer={[
            <Button key="cancel" onClick={() => {
              setShowAddForm(false);
              setSelectedBenefit(null);
              form.resetFields();
            }}>
              Annuler
            </Button>,
            <Button 
              key="submit" 
              type="primary" 
              onClick={() => form.submit()}
              loading={loading || apiLoading}
            >
              {selectedBenefit ? 'Modifier' : 'Créer'}
            </Button>
          ]}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={async (values) => {
              const benefitData: NaturaBenefit = {
                ...selectedBenefit,
                ...values,
                startDate: values.startDate?.format('YYYY-MM-DD'),
                endDate: values.endDate?.format('YYYY-MM-DD'),
              };
              await saveBenefit(benefitData);
              form.resetFields();
            }}
            initialValues={selectedBenefit ? {
              ...selectedBenefit,
              startDate: selectedBenefit.startDate ? dayjs(selectedBenefit.startDate) : undefined,
              endDate: selectedBenefit.endDate ? dayjs(selectedBenefit.endDate) : undefined,
            } : {}}
          >
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="benefitType"
                  label="Type d'avantage"
                  rules={[{ required: true, message: 'Veuillez sélectionner un type' }]}
                >
                  <Select placeholder="Sélectionner un type d'avantage">
                    {benefitTypes.map(type => (
                      <Option key={type.value} value={type.value}>
                        <Space>
                          <type.icon />
                          {type.label}
                        </Space>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="calculationMethod"
                  label="Méthode de calcul"
                  rules={[{ required: true, message: 'Veuillez sélectionner une méthode' }]}
                >
                  <Select placeholder="Méthode de calcul">
                    {calculationMethods.map(method => (
                      <Option key={method.value} value={method.value}>
                        {method.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: 'Veuillez saisir une description' }]}
            >
              <Input placeholder="Description de l'avantage" />
            </Form.Item>

            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="startDate" 
                  label="Date de début"
                  rules={[{ required: true, message: 'Date de début requise' }]}
                >
                  <DatePicker style={{ width: '100%' }} placeholder="Date de début" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="endDate" label="Date de fin">
                  <DatePicker style={{ width: '100%' }} placeholder="Date de fin (optionnelle)" />
                </Form.Item>
              </Col>
            </Row>

            {/* Champs spécifiques selon la méthode de calcul */}
            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => 
              prevValues.calculationMethod !== currentValues.calculationMethod
            }>
              {({ getFieldValue }) => {
                const calculationMethod = getFieldValue('calculationMethod');
                
                return (
                  <Row gutter={[16, 0]}>
                    {calculationMethod === 'historical_240' && (
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="historicalCost"
                          label="Coût historique (FCFA)"
                          rules={[{ required: true, message: 'Coût historique requis' }]}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            placeholder="Coût d'acquisition ou construction"
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, '')) || 0}
                          />
                        </Form.Item>
                      </Col>
                    )}
                    
                    {calculationMethod === 'actual_cost' && (
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="monthlyActualCost"
                          label="Coût mensuel réel (FCFA)"
                          rules={[{ required: true, message: 'Coût mensuel requis' }]}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            placeholder="Coût mensuel réel"
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, '')) || 0}
                          />
                        </Form.Item>
                      </Col>
                    )}
                    
                    {calculationMethod === 'fixed_amount' && (
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="fixedMonthlyAmount"
                          label="Montant mensuel fixe (FCFA)"
                          rules={[{ required: true, message: 'Montant fixe requis' }]}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            placeholder="Montant mensuel fixe"
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, '')) || 0}
                          />
                        </Form.Item>
                      </Col>
                    )}
                    
                    {calculationMethod === 'percentage' && (
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="percentageRate"
                          label="Pourcentage du salaire (%)"
                          rules={[{ required: true, message: 'Pourcentage requis' }]}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            placeholder="Pourcentage"
                            min={0}
                            max={100}
                            formatter={(value) => `${value}%`}
                            parser={(value) => {
                              const parsed = parseFloat(value!.replace('%', '')) || 0;
                              return Math.min(Math.max(parsed, 0), 100) as 0 | 100;
                            }}
                          />
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                );
              }}
            </Form.Item>

            {/* Champs spécifiques selon le type d'avantage */}
            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => 
              prevValues.benefitType !== currentValues.benefitType
            }>
              {({ getFieldValue }) => {
                const benefitType = getFieldValue('benefitType');
                
                if (!benefitType) return null;
                
                return (
                  <>
                    <Divider orientation="left">Détails spécifiques</Divider>
                    
                    {/* Logement de fonction */}
                    {benefitType === 'housing' && (
                      <Row gutter={[16, 0]}>
                        <Col xs={24} sm={8}>
                          <Form.Item name="housingSurfaceM2" label="Surface (m²)">
                            <InputNumber 
                              style={{ width: '100%' }} 
                              placeholder="Surface en m²" 
                              min={0}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item name="housingRoomsCount" label="Nombre de pièces">
                            <InputNumber 
                              style={{ width: '100%' }} 
                              placeholder="Nombre de pièces" 
                              min={1}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item name="housingAddress" label="Adresse">
                            <Input placeholder="Adresse du logement" />
                          </Form.Item>
                        </Col>
                      </Row>
                    )}

                    {/* Véhicule de fonction */}
                    {benefitType === 'vehicle' && (
                      <Row gutter={[16, 0]}>
                        <Col xs={24} sm={12}>
                          <Form.Item name="vehicleBrand" label="Marque">
                            <Input placeholder="Marque du véhicule" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item name="vehicleModel" label="Modèle">
                            <Input placeholder="Modèle du véhicule" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item name="vehicleYear" label="Année">
                            <InputNumber 
                              style={{ width: '100%' }} 
                              placeholder="Année de fabrication" 
                              min={1990}
                              max={new Date().getFullYear() + 1}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item name="vehicleRegistration" label="Immatriculation">
                            <Input placeholder="Numéro d'immatriculation" />
                          </Form.Item>
                        </Col>
                      </Row>
                    )}

                    {/* Domesticité */}
                    {benefitType === 'domestic_help' && (
                      <Row gutter={[16, 0]}>
                        <Col xs={24} sm={12}>
                          <Form.Item name="domesticEmployeeCount" label="Nombre d'employés">
                            <InputNumber 
                              style={{ width: '100%' }} 
                              placeholder="Nombre d'employés domestiques" 
                              min={1}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item name="domesticTotalSalary" label="Salaires totaux (FCFA)">
                            <InputNumber
                              style={{ width: '100%' }}
                              placeholder="Salaires mensuels totaux"
                              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                              parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item name="domesticCnssEmployer" label="CNSS Employeur (FCFA)">
                            <InputNumber
                              style={{ width: '100%' }}
                              placeholder="CNSS employeur"
                              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                              parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item name="domesticTpa" label="TPA (FCFA)">
                            <InputNumber
                              style={{ width: '100%' }}
                              placeholder="Taxe patronale d'apprentissage"
                              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                              parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item name="domesticOtherCosts" label="Autres coûts (FCFA)">
                            <InputNumber
                              style={{ width: '100%' }}
                              placeholder="Autres coûts mensuels"
                              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                              parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    )}

                    {/* Utilities */}
                    {benefitType === 'utilities' && (
                      <Row gutter={[16, 0]}>
                        <Col xs={24} sm={12}>
                          <Form.Item name="utilityType" label="Type d'utilité">
                            <Select placeholder="Sélectionner le type">
                              <Option value="water">Eau</Option>
                              <Option value="electricity">Électricité</Option>
                              <Option value="phone">Téléphone</Option>
                              <Option value="internet">Internet</Option>
                              <Option value="cable_tv">Télévision</Option>
                              <Option value="mixed">Mixte</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                    )}

                    {/* Scolarité */}
                    {benefitType === 'education' && (
                      <Row gutter={[16, 0]}>
                        <Col xs={24} sm={8}>
                          <Form.Item name="educationChildCount" label="Nombre d'enfants">
                            <InputNumber 
                              style={{ width: '100%' }} 
                              placeholder="Nombre d'enfants" 
                              min={1}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item name="educationAnnualCost" label="Coût annuel (FCFA)">
                            <InputNumber
                              style={{ width: '100%' }}
                              placeholder="Coût annuel total"
                              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                              parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Form.Item name="educationInstitution" label="Établissement">
                            <Input placeholder="Nom de l'établissement" />
                          </Form.Item>
                        </Col>
                      </Row>
                    )}
                  </>
                );
              }}
            </Form.Item>

            <Divider />

            {/* Options additionnelles */}
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12}>
                <Form.Item name="requiresApproval" valuePropName="checked">
                  <Checkbox>Nécessite une approbation</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="isActive" valuePropName="checked" initialValue={true}>
                  <Checkbox>Avantage actif</Checkbox>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      </Content>
    </Layout>
  );
};

export default NaturaBenefitsManager;
